import React, { ReactElement } from 'react'

import Company from '../../model/company'
import CompanySetting from '../../model/companySetting'
import { FormComponentProps, withValidations } from '../../utils/form-utils'
import { formatInputNumber, softForceParseInputNumber } from '../../utils/number-utils'
import { setByPath } from '../../utils/object-utils'
import { t } from '../../utils/translation-utils'
import Button from '../elements/button'
import Card from '../elements/card'
import Col from '../elements/grid/col'
import Row from '../elements/grid/row'
import Input from '../elements/input'
import Subtitle from '../elements/Subtitle'
import Title from '../elements/Title'
import SwitchWrapper from '../form-elements/SwitchWrapper'

type Props = {
  visible: boolean
  company: Company
}

type Fields = {
  DAUnionCode?: string
  enableYearlySalaryStatistics: boolean
  enableQuarterlySalaryStatistics: boolean
  enableAbsenceStatistics: boolean
  enableDAStatistics: boolean
}

export type AutomaticFields = {
  DAUnionCode?: number
  enableYearlySalaryStatistics: boolean
  enableQuarterlySalaryStatistics: boolean
  enableAbsenceStatistics: boolean
  settingsEnabled: CompanySetting[]
}

function ReportDSTAutomaticModal(props: Props & FormComponentProps<Fields, AutomaticFields>): ReactElement | null {
  const { decorateField } = props

  return (
    <Card className="company-report-dst">
      <Title>{t('pay_statistics.title')}</Title>
      <p style={{ fontSize: '14px' }}>{t('report_dst.automatic.note.line_1')}</p>
      <Subtitle>{t('pay_statistics.dst.title')}</Subtitle>
      <p style={{ fontSize: '14px' }}>{t('report_dst.automatic.note.line_2')}</p>
      <Row>
        <Col span={24}>
          {decorateField('DAUnionCode', {
            placeholder: t('report_dst.automatic.form.da_union_code'),
            validate: (val) => {
              if (!val || val === '') {
                return null
              }
              if (!val.match(/^[0-9]+$/) || val.length !== 4) {
                return t('report_dst.automatic.form.da_union_code.invalid')
              }
              return null
            },
          })(<Input />)}
        </Col>
      </Row>
      <Row>
        <Col span={12}>
          <SwitchWrapper<Fields>
            id={'enableYearlySalaryStatistics'}
            decorateField={decorateField}
            textStyle={{ fontSize: '14px' }}
          >
            {t('report_dst.automatic.form.enable_yearly_salary_statistics')}
          </SwitchWrapper>
        </Col>
        <Col span={12}>
          <SwitchWrapper<Fields>
            id={'enableQuarterlySalaryStatistics'}
            decorateField={decorateField}
            textStyle={{ fontSize: '14px' }}
          >
            {t('report_dst.automatic.form.enable_quarterly_salary_statistics')}
          </SwitchWrapper>
        </Col>
        <Col span={12}>
          <SwitchWrapper<Fields>
            id={'enableAbsenceStatistics'}
            decorateField={decorateField}
            textStyle={{ fontSize: '14px' }}
          >
            {t('report_dst.automatic.form.enable_absence_statistics')}
          </SwitchWrapper>
        </Col>
      </Row>
      <Subtitle style={{ marginTop: '24px' }}>{t('pay_statistics.da.title')}</Subtitle>
      <p style={{ fontSize: '14px' }}>{t('pay_statistics.da.note')}</p>
      <Row>
        <Col span={24}>
          <SwitchWrapper<Fields>
            id={'enableDAStatistics'}
            decorateField={decorateField}
            textStyle={{ fontSize: '14px' }}
          >
            {t('pay_statistics.da.enable')}
          </SwitchWrapper>
        </Col>
      </Row>

      <Row>
        <Col span={24}>
          <Button htmlType="submit" size="extra-extra-large" type="primary">
            {t('form.button.save_changes')}
          </Button>
        </Col>
      </Row>
    </Card>
  )
}

export default withValidations<Props, Fields, AutomaticFields>({
  mapPropsToFields: (props) => ({
    DAUnionCode: props.company.DAUnionCode ? formatInputNumber(props.company.DAUnionCode) : undefined,
    enableYearlySalaryStatistics: props.company.enableYearlySalaryStatistics,
    enableQuarterlySalaryStatistics: props.company.enableQuarterlySalaryStatistics,
    enableAbsenceStatistics: props.company.enableAbsenceStatistics,
    enableDAStatistics: props.company.settingsEnabled.includes('ContinuousSalaryStatistics'),
  }),
  onChange: (key, val) => {
    const values = {}
    setByPath(values, key, val)
    if (key === 'enableDAStatistics' && val === true) {
      setByPath(values, 'enableYearlySalaryStatistics', false)
      setByPath(values, 'enableQuarterlySalaryStatistics', false)
      setByPath(values, 'enableAbsenceStatistics', false)
    } else if (
      (key === 'enableYearlySalaryStatistics' ||
        key === 'enableQuarterlySalaryStatistics' ||
        key === 'enableAbsenceStatistics') &&
      val === true
    ) {
      setByPath(values, 'enableDAStatistics', false)
    }
    return values
  },
  onSubmit: (values, props) => {
    const base: CompanySetting[] = props.company.settingsEnabled.filter((s) => s !== 'ContinuousSalaryStatistics')
    const settingsEnabled: CompanySetting[] = values.enableDAStatistics
      ? [...base, 'ContinuousSalaryStatistics' as CompanySetting]
      : base
    return {
      DAUnionCode: softForceParseInputNumber(values.DAUnionCode),
      enableYearlySalaryStatistics: values.enableYearlySalaryStatistics,
      enableQuarterlySalaryStatistics: values.enableQuarterlySalaryStatistics,
      enableAbsenceStatistics: values.enableAbsenceStatistics,
      settingsEnabled,
    }
  },
})(ReportDSTAutomaticModal)
